/**
 * F-014 (session 3, 2026-05-11) — platform_admin impersonation banner.
 *
 * Background: when a platform_admin logs into Hank (a tenant-scoped UI),
 * the workspace shell renders but every tenant-scoped API call returns 403
 * because the impersonation token doesn't carry the right tenant claim.
 * The operator sees an empty / partially-broken workspace with no
 * explanation — see F-014 in docs/testing/findings-and-remediation.md.
 *
 * This banner is option (c) of the three F-014 design choices: accept the
 * current behavior, but surface it. Operators logged in as platform_admin
 * see an explicit "you're impersonating; tenant APIs are unavailable"
 * banner with guidance to switch accounts for tenant-scoped testing.
 *
 * Option (a) (Hank workspace redirect for platform_admin) and option (b)
 * (CP issues synthetic-tenant-claim impersonation tokens) remain open
 * future-arc improvements. This option ships in session 3 because it's
 * pure Hank UI (no hard-limit), low-risk, and immediately useful.
 *
 * The role comes from `getStoredAuth().role`, populated from the JWT
 * payload during login (see `auth.ts:buildStoredAuthFromPayload`).
 */

import React from "react";
import { getStoredAuth } from "./auth";

export default function PlatformAdminBanner(): React.ReactElement | null {
  const auth = getStoredAuth();
  if (auth.role !== "platform_admin") return null;

  return (
    <div
      role="alert"
      aria-label="Platform admin impersonation banner"
      style={{
        backgroundColor: "var(--c-warning-bg, #fef3c7)",
        color: "var(--c-warning-text, #92400e)",
        borderBottom: "1px solid var(--c-warning-border, #fbbf24)",
        padding: "0.5rem 1rem",
        fontSize: "0.75rem",
        lineHeight: "1.4",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
      }}
      data-testid="platform-admin-banner"
    >
      <span>
        <strong>Platform admin session.</strong> Tenant-scoped APIs are
        unavailable in this view ({auth.email ?? "support session"}). Log out
        and sign in as a tenant_owner to access the full workspace, or use
        Adam for tenant management.
      </span>
      <span style={{ opacity: 0.7, whiteSpace: "nowrap" }}>F-014</span>
    </div>
  );
}
